import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';
import { Box, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import useStepNavigationAmountStyles from '../MUI/StepNavigationAmount/makeStyles';
import StepNavigationButtons from '../MUI/StepNavigationButtons';
import AdditionalCoverages from '../AdditionalCoverages';
import Form from './Form';
import ScrollToTopPage from '../ScrollToTopPage';
import useDeviceResize from '../../hooks/useDeviceResize';
import useDefaultPlanSelection from '../../hooks/useDefaultPlanSelection';
import useDefaultLimitSelection from '../../hooks/useDefaultLimitSelection';
import useRegisterValidationFields from '../../hooks/useRegisterValidationFields';
import { getOnlyLiteDisallowedCountry, bindTravelAdditionalCoverages } from '../../helpers/travel/travelCalcHelpers';
import { EFES_CONTENT_WIDTH, FULL_WIDTH } from '../../constants/uiConstants';
import { CALCULATOR_TYPES } from '../../constants';
import {
  ONE_DAY,
  TRAVEL_MIN_DAYS,
  TRAVEL_PLANS,
  TRAVEL_PLANS_LABEL,
  TRAVEL_PRICE_KEYS,
} from '../../constants/travelCalcConstants';

const scrollTo = (elem, isCalculatorsPage) => {
  if (elem) {
    let scrollToPosition = elem.offsetTop;
    // adjusting different header sizes based on viewport
    if (isCalculatorsPage) {
      if (window.innerWidth < 1280) {
        scrollToPosition -= 40;
      } else {
        scrollToPosition -= 60;
      }
    }
    if (!isCalculatorsPage) {
      if (window.innerWidth < 1280) {
        scrollToPosition -= 70;
      }
    }

    window.scrollTo(0, scrollToPosition);
  }
};

const TravelCalculator = ({
  age,
  setAge,
  showAgeInput,
  travelDetails,
  prices,
  setSelectedPlan,
  onCoverageChange,
  setSelectedLimit,
  setRegion,
  setDestinations,
  setDays,
  setRange,
  setSelectedPackage,
  formProps,
  hasNarrowView,
  plansWithAllowedLimitRegionAndAge,
  amount,
  handleOpenMobileSummary,
  buyButtonHandler,
  showNavigationButtons,
  fullWidthElement,
  actionName,
  totalPriceAfterDiscount,
}) => {
  const {
    region,
    days,
    selectedPlan,
    selectedCoverages,
    isFetchingPrice,
    selectedLimit,
    selectedDestinations,
  } = travelDetails;

  const { t } = useTranslation();
  const stepNavigationAmountClasses = useStepNavigationAmountStyles();
  const isDeviceLgSize = useDeviceResize('extraLgSize');
  const [showTravelPlans, setShowTravelPlans] = useState(false);
  const contractData = useSelector((state) => state.travelContract.contractData, shallowEqual);
  const [defaultMinDays, setDefaultMinDays] = useState(TRAVEL_MIN_DAYS);
  const travelPlans = useRef();
  const scrolledOnce = useRef();

  const { errors, register, setValue } = formProps;
  const discounts = [];

  const hidePrices = Boolean(
    errors.age || errors.region || errors.start || errors.end || errors.days,
  );

  const planCoverages = useMemo(() => (
    bindTravelAdditionalCoverages(selectedPlan)
  ), [selectedPlan]);

  const planPrices = useMemo(() => (
    prices[selectedPlan] ? Object.values(prices[selectedPlan]) : []
  ), [prices, selectedPlan]);

  const hasOnlyLiteDisallowedCountry = useMemo(
    () => getOnlyLiteDisallowedCountry(selectedDestinations), [selectedDestinations],
  );

  useRegisterValidationFields([
    'age',
    'region',
    'start',
    'end',
    'days',
    'defaultMinDays',
    'hasOnlyLiteDisallowedCountry',
  ], register);

  useDefaultPlanSelection(
    showTravelPlans,
    region,
    age,
    plansWithAllowedLimitRegionAndAge,
    selectedPlan,
    setSelectedPlan,
    TRAVEL_PLANS,
  );

  useDefaultLimitSelection(region, selectedPlan, setSelectedLimit, selectedLimit, TRAVEL_PLANS);

  useEffect(() => {
    const minDays = (
      hasOnlyLiteDisallowedCountry && selectedPlan === TRAVEL_PLANS_LABEL.GEORGIA
    ) ? ONE_DAY : TRAVEL_MIN_DAYS;
    setValue('defaultMinDays', minDays);
    setDefaultMinDays(minDays);
    setValue('hasOnlyLiteDisallowedCountry', hasOnlyLiteDisallowedCountry && selectedPlan === TRAVEL_PLANS_LABEL.GEORGIA);
  }, [days, hasOnlyLiteDisallowedCountry, setValue, selectedPlan]);

  useEffect(() => {
    setShowTravelPlans(Boolean(age && region && days));
  }, [age, region, days]);

  useEffect(() => {
    if (showTravelPlans && !scrolledOnce.current && !selectedPlan) {
      scrolledOnce.current = true;
      setTimeout(() => {
        // Age Input is only visible in calculators page
        scrollTo(travelPlans.current, showAgeInput);
      }, 500);
    }
  }, [showTravelPlans, scrolledOnce, selectedPlan, showAgeInput]);

  const getSingleCoverageDiscount = useCallback(
    (coverageId) => discounts && discounts.reduce((acc, el) => acc + el.coverages[coverageId], 0),
    [discounts],
  );

  const getSingleCoveragePrice = useCallback(
    (coverageId) => planPrices?.reduce(
      (acc, el) => acc + el.coverages[coverageId], 0,
    ),
    [planPrices],
  );

  return (
    <Box>
      <ScrollToTopPage />
      <Form
        formProps={formProps}
        setAge={setAge}
        age={age}
        setDestinations={setDestinations}
        setRegion={setRegion}
        setSelectedLimit={setSelectedLimit}
        setSelectedPlan={setSelectedPlan}
        travelDetails={travelDetails}
        setDays={setDays}
        setRange={setRange}
        setSelectedPackage={setSelectedPackage}
        plansWithAllowedLimitRegionAndAge={plansWithAllowedLimitRegionAndAge}
        showAgeInput={showAgeInput}
        fullWidthElement={fullWidthElement}
        showTravelPlans={showTravelPlans}
        prices={prices}
        isFetchingPrice={isFetchingPrice}
        priceKeys={TRAVEL_PRICE_KEYS}
        amount={amount}
        actionName={actionName}
        defaultMinDays={defaultMinDays}
        hasOnlyLiteDisallowedCountry={hasOnlyLiteDisallowedCountry}
        onCoverageChange={onCoverageChange}
      />
      {showTravelPlans && (
        selectedPlan === TRAVEL_PLANS.ELITE.id || selectedPlan === TRAVEL_PLANS.GEORGIA.id
      ) && (
      <AdditionalCoverages
        type={CALCULATOR_TYPES.TRAVEL}
        coverages={planCoverages}
        contractData={contractData}
        priceKeys={TRAVEL_PRICE_KEYS}
        selectedCoverages={selectedCoverages}
        onCoverageChange={onCoverageChange}
        hidePrice={hidePrices}
        isFetchingPrice={isFetchingPrice}
        hasNarrowView={hasNarrowView}
        getSingleCoveragePrice={getSingleCoveragePrice}
        getSingleCoverageDiscount={getSingleCoverageDiscount}
      />
      )}
      {showNavigationButtons && isDeviceLgSize && (
        <Grid
          container
          className={classNames({
            [stepNavigationAmountClasses.mobileNavigation]: isDeviceLgSize,
          })}
          spacing={isDeviceLgSize ? 4 : 0}
        >
          <Grid item style={{ width: isDeviceLgSize ? FULL_WIDTH : EFES_CONTENT_WIDTH }}>
            <Box>
              <StepNavigationButtons
                btnText={t('buyOnline')}
                amount={totalPriceAfterDiscount}
                handleOpenMobileSummary={handleOpenMobileSummary}
                buttonHandler={buyButtonHandler}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

TravelCalculator.propTypes = {
  showAgeInput: PropTypes.bool,
  age: PropTypes.number,
  setAge: PropTypes.func,
  travelDetails: PropTypes.shape({
    region: PropTypes.string,
    selectedDestinations: PropTypes.arrayOf(PropTypes.shape({})),
    range: PropTypes.shape({}),
    days: PropTypes.number,
    selectedPlan: PropTypes.string,
    selectedCoverages: PropTypes.shape({
      SPO: PropTypes.shape({
        id: PropTypes.string,
        active: PropTypes.bool,
      }),
      LUG: PropTypes.shape({
        id: PropTypes.string,
        active: PropTypes.bool,
      }),
      INC: PropTypes.shape({
        id: PropTypes.string,
        active: PropTypes.bool,
      }),
      CA: PropTypes.shape({
        id: PropTypes.string,
        active: PropTypes.bool,
        children: PropTypes.shape({}),
      }),
      PR: PropTypes.shape({
        id: PropTypes.string,
        active: PropTypes.bool,
      }),
      CAS: PropTypes.shape({
        id: PropTypes.string,
        active: PropTypes.bool,
      }),
    }),
    selectedPackage: PropTypes.shape({}),
    isFetchingPrice: PropTypes.bool,
    selectedLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]).isRequired,
  }).isRequired,
  prices: PropTypes.shape({}).isRequired,
  setRegion: PropTypes.func.isRequired,
  setDestinations: PropTypes.func.isRequired,
  setDays: PropTypes.func.isRequired,
  setRange: PropTypes.func.isRequired,
  setSelectedLimit: PropTypes.func.isRequired,
  setSelectedPlan: PropTypes.func.isRequired,
  onCoverageChange: PropTypes.func.isRequired,
  setSelectedPackage: PropTypes.func.isRequired,
  formProps: PropTypes.shape({
    errors: PropTypes.shape({
      age: PropTypes.shape({}),
      region: PropTypes.shape({}),
      start: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      end: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      days: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
    setValue: PropTypes.func,
    register: PropTypes.func,
  }).isRequired,
  hasNarrowView: PropTypes.bool,
  showNavigationButtons: PropTypes.bool,
  amount: PropTypes.number,
  plansWithAllowedLimitRegionAndAge: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleOpenMobileSummary: PropTypes.func,
  buyButtonHandler: PropTypes.func,
  fullWidthElement: PropTypes.bool,
  actionName: PropTypes.string.isRequired,
  totalPriceAfterDiscount: PropTypes.number,
};

TravelCalculator.defaultProps = {
  showAgeInput: true,
  age: null,
  setAge: null,
  buyButtonHandler: null,
  showNavigationButtons: true,
  hasNarrowView: false,
  fullWidthElement: false,
  handleOpenMobileSummary: () => { },
  amount: 0,
  totalPriceAfterDiscount: 0,
};

export default TravelCalculator;
